import React, { useCallback, useRef, useState, useMemo } from 'react';
import { joinShareUrl } from '../services/shareService';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Settings2, Lock, MapPin, User as UserIcon, PawPrint, Package, Check, Circle, MoreVertical, Tv } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { TopBar, Btn, Pill, Avatar, IconButton, CastModal, Kbd, Page, RosterRail, VisibilityCard, useProfilePreview, useToast } from '../components/wr';
import { useWebSocket } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import { useHotkeys } from '../hooks/useHotkeys';
import { useSurface } from '../hooks/useSurface';
import { useTvConnected } from '../hooks/useTvConnected';
import { websocketService } from '../services/websocketService';
import { HouseRulesSettings, defaultHouseRules } from '../components/HouseRules';

type BotDifficulty = 'easy' | 'medium' | 'hard' | 'expert' | 'insane';

const BOT_DIFFICULTIES: Array<{ value: BotDifficulty; label: string; hint: string }> = [
  { value: 'easy',   label: 'Taking it easy', hint: 'Common words. Duplicates you a lot.' },
  { value: 'medium', label: 'Playing along',  hint: 'A fair sibling.' },
  { value: 'hard',   label: 'Really trying',  hint: 'Uncommon words. Rarely blanks.' },
  { value: 'expert', label: 'Showing off',    hint: 'Fast and obscure.' },
  { value: 'insane', label: 'Merciless',      hint: 'The menace.' },
];

interface Player {
  id: string;
  name: string;
  avatar: string;
  isReady: boolean;
  isHost: boolean;
  score?: number;
  isBot?: boolean;
  isConnected?: boolean;
}

interface GameSettings {
  rounds: number;
  timeLimit: number;
  categories: string[];
  mode: 'standard' | 'fast' | 'ultra' | 'daily' | 'custom';
  isSinglePlayer?: boolean;
  houseRules?: HouseRulesSettings;
  /** `true` = invite-only. Server-owned; see GameContext.GameSettings. */
  isPrivate?: boolean;
}

interface LobbyScreenProps {
  roomCode: string;
  isHost: boolean;
  players: Player[];
  gameSettings?: GameSettings;
  onStartGame: () => void;
  onLeaveRoom: () => void;
  onPlayerReady?: (isReady: boolean) => Promise<void>;
  onUpdateSettings?: (settings: Partial<GameSettings>) => void;
  currentPlayer?: Player | null;
  isLoading?: boolean;
}

const DEFAULT_CATEGORIES = ['Name', 'Place', 'Animal', 'Thing'];
const CATEGORY_ICONS: Record<string, React.FC<{ size?: number }>> = {
  Name: ({ size = 12 }) => <UserIcon size={size} />,
  Place: ({ size = 12 }) => <MapPin size={size} />,
  Animal: ({ size = 12 }) => <PawPrint size={size} />,
  Thing: ({ size = 12 }) => <Package size={size} />,
};

/**
 * Lobby — pre-game gathering. Big tappable room code, branded avatars in
 * the player list, and a host-only Start button. Settings live in a slide-up
 * sheet so the lobby itself stays calm.
 */
export default function LobbyScreen({
  roomCode = 'ROOM',
  isHost,
  players,
  gameSettings: initialSettings,
  onStartGame,
  onLeaveRoom,
  onPlayerReady,
  onUpdateSettings,
  currentPlayer,
  isLoading,
}: LobbyScreenProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const { kickPlayer, transferHost, addBot } = useWebSocket();
  const [showSettings, setShowSettings] = useState(false);
  const [showCast, setShowCast] = useState(false);
  const [copyHint, setCopyHint] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [addingBot, setAddingBot] = useState(false);
  const [showBotPicker, setShowBotPicker] = useState(false);
  /** The one surface branch: whether there is an aside column. */
  const desktop = useSurface() === 'desktop';
  /** Is the family's television already watching this room? */
  const tvConnected = useTvConnected(roomCode);
  const codeRef = useRef<HTMLDivElement | null>(null);

  const gs: GameSettings = initialSettings || {
    rounds: 5,
    timeLimit: 60,
    categories: DEFAULT_CATEGORIES,
    mode: 'standard',
    houseRules: defaultHouseRules,
  };

  // Derive `me` from the room's players list so the ready toggle picks up
  // server state updates. The `currentPlayer` prop comes from auth and is
  // a separate object — its isReady never updates after a player_ready
  // event, which is what made the Ready button look stuck.
  const meId = currentPlayer?.id;
  const me = useMemo(() => {
    if (!meId) return currentPlayer || null;
    return players.find(p => p.id === meId) || currentPlayer || null;
  }, [players, meId, currentPlayer]);
  const meReady = me?.isReady ?? false;
  const readyCount = players.filter(p => p.isReady).length;
  const canStart = isHost && players.length >= 1 && !starting;

  const formattedCode = useMemo(() => formatCode(roomCode), [roomCode]);
  /*
   * The share URL must be the PUBLIC origin, not `window.location.origin`.
   *
   * On localhost that produced `http://localhost:8001/join/X7K2`, which is a
   * link to the sender's own laptop — useless to the person receiving it. And it
   * is only the public origin that Firebase Hosting rewrites to the backend, so
   * only the public origin unfurls with "Maya invited you to a table" and a card
   * instead of a bare grey rectangle.
   */
  const joinUrl = useMemo(() => joinShareUrl(roomCode), [roomCode]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopyHint('Copied!');
      setTimeout(() => setCopyHint(null), 1500);
    } catch {
      setCopyHint('Copy failed');
    }
  };

  /**
   * Click the code → copy the link AND select the glyphs.
   *
   * The selection isn't decoration. `navigator.clipboard` is blocked in
   * plenty of real situations (insecure origin, denied permission, an
   * enterprise policy), and when it fails the desktop user's instinct is
   * Ctrl+C — which does nothing against unselected text. Selecting it means
   * the fallback path works without us doing anything.
   */
  const handleCodeClick = useCallback(() => {
    const el = codeRef.current;
    if (el && typeof window !== 'undefined') {
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    handleCopy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinUrl]);

  const handleShare = async () => {
    // The link now carries its own preview (og:title, og:image — see
    // backend/src/share/shareRoutes.js), so the text can stop doing the
    // preview's job and just be a human sentence.
    const text = `Join my WordRush table — code ${formattedCode}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'WordRush', text, url: joinUrl }); return; } catch {}
    }
    handleCopy();
  };

  const handleReady = async () => {
    if (!onPlayerReady) return;
    try { await onPlayerReady(!meReady); } catch (e) { console.error(e); }
  };

  const handleStart = async () => {
    if (!canStart) return;
    setStarting(true);
    try { await onStartGame(); } catch (e) { console.error(e); setStarting(false); }
  };

  /**
   * Add Atom at a chosen difficulty. The old version called `addBot()` with
   * NO arguments — so all five supported difficulties collapsed into the
   * server's default and the setting may as well not have existed.
   */
  const handleAddBot = async (difficulty: BotDifficulty) => {
    if (addingBot) return;
    setShowBotPicker(false);
    setAddingBot(true);
    try {
      await addBot(difficulty);
    } catch (e: any) {
      toast.error(e?.message || 'Could not add Atom. Try again?');
    } finally {
      setAddingBot(false);
    }
  };

  // Lobby management — kick + transfer host. The host is the only one who
  // can fire these; the UI hides the buttons for non-hosts. We surface
  // them as a small menu on each player row to keep the lobby calm.
  //
  // These used to raw-emit straight onto the socket and swallow every
  // failure into a console.warn: the host tapped "Make host", nothing
  // happened, and nobody was told why. They route through the service now,
  // which resolves on the server's ack and rejects (visibly) otherwise.
  const [actionFor, setActionFor] = useState<Player | null>(null);
  const { open: openPreview } = useProfilePreview();

  /**
   * "Give me another one."
   *
   * A guest is HANDED a name at the door — "Kind Otter" — and the lobby is
   * where they first sit and look at it in front of their family. The server has
   * been able to deal a fresh one this whole time (it screens it, de-dupes it
   * against the table, and tells the room); nothing ever asked. Two dead code
   * paths existed for this, a REST route and a socket handler, and the lobby
   * used neither. The REST one is gone; this is the other one, finally wired.
   */
  const { isGuest } = useAuth();
  const [rerolling, setRerolling] = useState(false);

  const handleReroll = async () => {
    if (rerolling) return;
    setRerolling(true);
    try {
      // Use the name the SERVER settled on — two Kind Otters at one table gets
      // de-duped, so what we asked for is not necessarily what we got.
      const name = await websocketService.rerollName();
      if (name) toast.success(`You're ${name} now.`);
    } catch {
      toast.error("Couldn't change your name. Try again?");
    } finally {
      setRerolling(false);
    }
  };

  const handleKick = async (player: Player) => {
    setActionFor(null);
    try {
      await kickPlayer(player.id);
      toast.success(`${player.name} was removed.`);
    } catch (e: any) {
      toast.error(e?.message || `Couldn't remove ${player.name}.`);
    }
  };

  const handleTransferHost = async (player: Player) => {
    setActionFor(null);
    try {
      await transferHost(player.id);
      toast.success(`${player.name} is the host now.`);
    } catch (e: any) {
      toast.error(e?.message || `Couldn't make ${player.name} host.`);
    }
  };

  /**
   * ⏎ starts the game. Host only, obviously — and useHotkeys already refuses
   * to fire while any sheet or dialog is open, so hitting Enter with the
   * settings card focused doesn't launch the round out from under you.
   */
  useHotkeys(
    [{ keys: 'Enter', handler: () => { void handleStart(); }, enabled: canStart && !isLoading }],
    desktop && isHost,
  );

  /**
   * THE LEAN-BACK ANSWER, and the whole mechanism.
   *
   * A laptop propped up for the living room is a real thing people do — and
   * the product for it already exists at `/tv/:code`. What we will NOT do is
   * GUESS that's what's happening: no idle-mouse heuristic, no fullscreen
   * detection. A wrong lean-back guess in a family product is a trust breach
   * — the screen you were reading suddenly becomes a scoreboard. So the user
   * says so, with one click, at the exact moment the decision is being made:
   * in the lobby, while everyone is joining.
   */
  const tvHandoff = (
    <button
      type="button"
      onClick={() => navigate(`/tv/${roomCode}`)}
      className="wr-row gap-2"
      style={{
        minHeight: 44,
        padding: '0 4px',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--ink-3)',
        fontFamily: 'var(--ui)',
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      <Tv size={14} />
      <span>Use this screen as the table display →</span>
    </button>
  );

  const roster = (
    <RosterRail
      players={players}
      meId={me?.id}
      showReady
      title={`Players · ${players.length} / 8`}
      // A public lobby is where you first meet a stranger. Block/Report has to
      // be reachable before the game starts, not only after it has gone wrong.
      roomCode={roomCode}
      footer={
        <>
          {isHost && players.length < 8 && (
            <Btn kind="ghost" full icon="Bot" loading={addingBot} onClick={() => setShowBotPicker(true)}>
              Add Atom
            </Btn>
          )}
          {/* Only a guest sees this. Someone who signed up CHOSE their name, and
              offering to randomise it would be an odd thing to say to them. */}
          {isGuest && (
            <Btn kind="ghost" full icon="Dices" loading={rerolling} onClick={handleReroll}>
              New name
            </Btn>
          )}
        </>
      }
    />
  );

  return (
    <Page variant={desktop ? 'max' : 'content'} className="lobby-screen">
      <>
        <TopBar
          left={<IconButton icon="ChevronLeft" ariaLabel="Leave" onClick={onLeaveRoom} />}
          center="Your room"
          right={
            <div className="wr-row gap-1" style={{ alignItems: 'center' }}>
              {/* A television that IS connected has to say so, right here.
                  Otherwise the host has no way to tell the difference between
                  "the TV is showing our room" and "the TV is showing the
                  Chromecast wallpaper and I have been talking to a screensaver
                  for two minutes". */}
              {tvConnected && (
                <div
                  className="wr-row gap-1"
                  style={{
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: 'var(--accent-soft)',
                    color: 'var(--accent)',
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  📺 TV ON
                </div>
              )}
              <IconButton
                icon="Cast"
                ariaLabel={tvConnected ? 'TV connected' : 'Put the game on the TV'}
                onClick={() => setShowCast(true)}
              />
              {/* The settings sheet is the PHONE's affordance. On a laptop the
                  same panel is a static card in the aside — there's room for
                  it, and burying host controls in a sheet on a 1280px canvas
                  is theatre. */}
              {isHost && !desktop && (
                <IconButton icon="Settings2" ariaLabel="Settings" onClick={() => setShowSettings(true)} />
              )}
            </div>
          }
        />

        {/* ⚠️ NO WRAPPER ON A PHONE. This started life as
            `<div className={desktop ? 'wr-lobby' : 'wr-col'} style={{flex:1}}>`
            wrapping the stage on BOTH surfaces — which looked harmless and was
            not. `.screen-pad` is a flex column and `.topbar` is `flex: 0 1 auto`,
            so when the lobby's content overflows (it does, on a 393×800 phone)
            the topbar SHRINKS to its 56px min-height. Adding a `min-height: 0`
            flex wrapper changed that flex math, the topbar sprang back to its
            natural 70px, and every pixel below it moved down 14px. Caught by
            the before/after pixel diff, which is exactly what it's for.

            So: on a phone the tree is byte-for-byte what it was. The grid only
            exists when there is actually a second column to put in it. */}
        <StageOrColumn desktop={desktop}>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="wr-col gap-4"
          style={{ flex: 1 }}
        >
          {/* Room code card. The QR is not decoration: Grandma joins with her
              camera, not by squinting at six characters and typing them into
              a browser bar. Point phone, tap notification, in the room. */}
          <div
            className="card lg"
            style={{
              background: 'linear-gradient(155deg, var(--surface), var(--bg-2))',
              textAlign: 'center',
            }}
          >
            <div className="eyebrow accent">Room code</div>
            {/* Click = copy the join link AND select the glyphs, so Ctrl+C
                works even when the clipboard API is blocked. */}
            <div
              ref={codeRef}
              role="button"
              tabIndex={0}
              onClick={handleCodeClick}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCodeClick(); } }}
              aria-label={`Room code ${formattedCode}. Click to copy the join link.`}
              title="Click to copy the join link"
              className="wr-roomcode"
              style={{
                fontFamily: 'var(--display)',
                fontWeight: 800,
                // Phone: locked to today's 32px. Desktop: no inline size at
                // all, so `.wr-roomcode`'s --t-code-lg (~72px) takes over. An
                // inline style would beat the class, so it must be absent.
                fontSize: desktop ? undefined : 32,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                marginTop: 4,
                cursor: 'pointer',
              }}
            >
              {formattedCode}
            </div>

            <div className="wr-row wr-center mt-3">
              <div
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  padding: 10,
                  border: '1px solid var(--line)',
                  lineHeight: 0,
                }}
              >
                <QRCodeSVG value={joinUrl} size={desktop ? 220 : 128} level="M" bgColor="#ffffff" fgColor="#16161a" />
              </div>
            </div>
            <div className="wr-text-3 mt-2" style={{ fontSize: 12 }}>
              Point a camera at this to join
            </div>
            {/* On a laptop the URL is typeable, so show it. On a phone it's
                noise — you'd never retype it, you'd tap Share. */}
            {desktop && (
              <div
                className="mt-2"
                style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink-3)', wordBreak: 'break-all' }}
              >
                {joinUrl.replace(/^https?:\/\//, '')}
              </div>
            )}

            <div className="wr-row wr-center gap-2 mt-3">
              <Btn kind="ghost" icon="Share2" onClick={handleShare}>Share</Btn>
              <Btn kind="ghost" icon="Copy" onClick={handleCopy}>
                {copyHint || 'Copy'}
              </Btn>
            </div>
          </div>

          {/* WHO CAN JOIN — directly under the code and the share buttons, and
              that placement is the argument.

              This is not a scoring rule and it does not belong in the settings
              sheet with the squeeze timer. It is the answer to "who is about to
              walk through the door", and the moment the host is asking that
              question is the moment they are looking at the share link. So it
              lives here, one tap, no sheet.

              It renders for EVERYONE, not just the host. A player has a right to
              know the room they are sitting in is listed to strangers — hiding
              the state from non-hosts would make the control a lie. The host
              gets the toggle; everyone else gets the truth. */}
          <VisibilityCard
            isPrivate={gs.isPrivate !== false}
            isHost={isHost}
            onChange={next => {
              // Straight to the server, which is the only source of truth. It
              // re-broadcasts `gameSettingsUpdated` to the whole lobby AND
              // re-broadcasts the public-rooms list, so the room appears in (or
              // vanishes from) Public Games without anyone refreshing.
              websocketService.updateGameSettings({ isPrivate: next });
              onUpdateSettings?.({ isPrivate: next });
            }}
          />

          {/* Player list — PHONE ONLY. On a laptop the same players are the
              standing roster rail on the right, where they stay put through
              the race and the tally too, so the room feels continuous. */}
          {!desktop && (
          <div className="wr-col gap-3" style={{ flex: 1, minHeight: 0 }}>
            <div className="wr-between">
              <span className="eyebrow">Players · {players.length} / 8</span>
              <Pill kind="green">
                <Circle size={6} fill="currentColor" /> live
              </Pill>
            </div>
            <div className="wr-col gap-2" style={{ overflowY: 'auto', flex: 1 }}>
              <AnimatePresence>
                {players.map(p => {
                  // isConnected is undefined on older payloads — treat it as
                  // online to avoid false greys. Bots always render online.
                  const online = p.isBot || p.isConnected !== false;
                  const canManage = isHost && p.id !== me?.id; // host manages everyone but themselves
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="prow"
                      style={{ opacity: online ? 1 : 0.55, cursor: 'pointer' }}
                      onClick={() => openPreview(p.id, { contextType: 'game', contextId: roomCode })}
                      role="button"
                      aria-label={`View ${p.name}'s profile`}
                    >
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <Avatar id={p.avatar} size={40} host={p.isHost} />
                        <span
                          aria-label={online ? 'online' : 'offline'}
                          title={online ? 'Online' : 'Reconnecting…'}
                          style={{
                            position: 'absolute',
                            right: -2,
                            bottom: -2,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: online ? 'var(--green, #1f8f4a)' : 'var(--ink-3)',
                            border: '2px solid var(--surface)',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.05)',
                          }}
                        />
                      </div>
                      <div className="body">
                        <div className="name">
                          {p.name}
                          {p.id === me?.id && <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}> (you)</span>}
                          {p.isBot && (
                            <Pill kind="ghost" className="mt-0" style={{ marginLeft: 6, fontSize: 10 }}>BOT</Pill>
                          )}
                        </div>
                        {p.isHost && <Pill kind="accent" className="mt-1">host</Pill>}
                        {!online && !p.isBot && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>Reconnecting…</div>
                        )}
                      </div>
                      {p.isReady ? (
                        <span className="ready"><Check size={12} strokeWidth={3} /> READY</span>
                      ) : (
                        <span className="waiting">{online ? 'Waiting…' : 'Away'}</span>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setActionFor(p); }}
                          aria-label={`Manage ${p.name}`}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 6,
                            cursor: 'pointer',
                            color: 'var(--ink-3)',
                            borderRadius: 8,
                            flexShrink: 0,
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              {isHost && players.length < 8 && (
                <Btn kind="ghost" full icon="Bot" loading={addingBot} onClick={() => setShowBotPicker(true)}>
                  Add Atom (AI bot)
                </Btn>
              )}
            </div>
          </div>
          )}

          {/* Settings summary. On desktop the live settings PANEL is in the
              aside, so a summary of it would just be the same words twice. */}
          {!desktop && (
          <div
            className="card"
            style={{
              background: 'var(--bg-2)',
              boxShadow: 'none',
              padding: 14,
            }}
          >
            <div className="wr-row gap-2" style={{ fontSize: 12, color: 'var(--ink-3)', flexWrap: 'wrap' }}>
              <Settings2 size={14} />
              <span>
                <b style={{ color: 'var(--ink)' }}>{gs.rounds} rounds</b> · {gs.timeLimit}s ·{' '}
                {gs.categories.length} categories · {gs.houseRules?.squeezeSeconds ?? 10}s squeeze
                {gs.houseRules?.strictScoring ? ' · strict scoring' : ''}
              </span>
            </div>
          </div>
          )}

          {/* Bottom action.
              Docked, not in flow. The player list above is its own scroll
              container (flex:1 + overflow:auto), so as the roster grows it eats
              the height and pushes whatever follows it off the bottom — under
              the docked nav, which then swallows every click. With two players
              in the lobby the host literally could not start the game.
              A primary CTA must always be reachable. Stick it above the nav. */}
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              paddingTop: 8,
              marginTop: 'auto',
              background: 'linear-gradient(to top, var(--bg) 72%, transparent)',
            }}
          >
          {isHost ? (
            <Btn kind="primary" size="xl" full icon="Play" loading={starting} disabled={!canStart || isLoading} onClick={handleStart}>
              <span className="wr-row gap-2" style={{ alignItems: 'center' }}>
                Start Game <Kbd onAccent>⏎</Kbd>
              </span>
            </Btn>
          ) : (
            <Btn
              kind={meReady ? 'dark' : 'primary'}
              size="xl"
              full
              icon={meReady ? 'Check' : 'CircleCheck'}
              onClick={handleReady}
            >
              {meReady ? "I'm ready ✓" : "I'm ready"}
            </Btn>
          )}
          </div>

          {/* The lean-back door. Deliberately in the flow, not in a menu:
              this is the moment the decision gets made. */}
          {desktop && tvHandoff}
        </motion.div>

        {/* ---- The aside: the table, and (for the host) the rules. ---- */}
        {desktop && (
          <aside className="lobby-aside">
            {roster}
            {isHost && (
              <div className="card lg">
                <div className="wr-row gap-2" style={{ alignItems: 'center', marginBottom: 4 }}>
                  <Settings2 size={14} color="var(--ink-3)" />
                  <span className="eyebrow">House rules</span>
                </div>
                <SettingsPanel gs={gs} onUpdateSettings={onUpdateSettings} />
              </div>
            )}
          </aside>
        )}
        </StageOrColumn>
      </>

      {/* Player action sheet (host kick + transfer host). Tapping a player
          row opens this; non-hosts never see it. */}
      <AnimatePresence>
        {actionFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActionFor(null)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 60,
              background: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 32, stiffness: 320 }}
              onClick={e => e.stopPropagation()}
              className="card lg"
              style={{
                width: 'calc(100% - 32px)',
                maxWidth: 440,
                margin: 16,
                background: 'var(--surface)',
                padding: 22,
              }}
            >
              <div className="wr-row gap-3" style={{ alignItems: 'center', marginBottom: 12 }}>
                <Avatar id={actionFor.avatar} size={44} host={actionFor.isHost} />
                <div style={{ flex: 1 }}>
                  <div className="h3" style={{ fontSize: 17 }}>{actionFor.name}</div>
                  <div className="wr-text-3" style={{ fontSize: 12 }}>
                    {actionFor.isBot ? 'AI bot · cannot be host' : actionFor.isHost ? 'Host' : 'Player'}
                  </div>
                </div>
              </div>
              <div className="wr-col gap-2 mt-3">
                {!actionFor.isBot && !actionFor.isHost && (
                  <Btn
                    kind="dark"
                    full
                    icon="Crown"
                    onClick={() => handleTransferHost(actionFor)}
                  >
                    Make host
                  </Btn>
                )}
                <Btn
                  kind="ghost"
                  full
                  icon="X"
                  onClick={() => handleKick(actionFor)}
                  style={{ color: 'var(--coral, #b94a3b)' }}
                >
                  {actionFor.isBot ? 'Remove bot' : 'Kick from room'}
                </Btn>
                <Btn kind="ghost" full onClick={() => setActionFor(null)}>
                  Cancel
                </Btn>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Atom difficulty picker. Five levels are supported end-to-end; the
          lobby used to expose none of them. Difficulty is phrased in-fiction
          — Atom is one character who plays harder, not five characters. */}
      <AnimatePresence>
        {showBotPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowBotPicker(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 60,
              background: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 32, stiffness: 320 }}
              onClick={e => e.stopPropagation()}
              className="card lg"
              style={{
                width: 'calc(100% - 32px)',
                maxWidth: 440,
                margin: 16,
                background: 'var(--surface)',
                padding: 22,
                maxHeight: '80vh',
                overflowY: 'auto',
              }}
            >
              <div className="eyebrow accent">ADD ATOM</div>
              <div className="h3" style={{ marginTop: 4 }}>How hard should Atom play?</div>
              <div className="wr-col gap-2 mt-3">
                {BOT_DIFFICULTIES.map(d => (
                  <button
                    key={d.value}
                    type="button"
                    className="prow"
                    onClick={() => handleAddBot(d.value)}
                    style={{
                      cursor: 'pointer',
                      border: '1.5px solid var(--surface-2)',
                      background: 'var(--surface)',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <Avatar id="atom" size={36} />
                    <div className="body">
                      <div className="name">{d.label}</div>
                      <div className="word" style={{ fontSize: 12 }}>{d.hint}</div>
                    </div>
                  </button>
                ))}
                <Btn kind="ghost" full onClick={() => setShowBotPicker(false)}>Cancel</Btn>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings sheet */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              background: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'flex-end',
            }}
            onClick={() => setShowSettings(false)}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--bg)',
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                maxHeight: '90vh',
                overflowY: 'auto',
              }}
            >
              <div className="screen-pad" style={{ paddingTop: 12 }}>
                <TopBar
                  left={<IconButton icon="X" ariaLabel="Close" onClick={() => setShowSettings(false)} />}
                  center="Settings"
                />
                <SettingsPanel gs={gs} onUpdateSettings={onUpdateSettings} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {showCast && <CastModal roomCode={roomCode} onClose={() => setShowCast(false)} />}
    </Page>
  );
}

/**
 * The two-column grid — and, on a phone, NOTHING AT ALL.
 *
 * Not "a grid that collapses to one column": no element. `.screen-pad` is a
 * flex column whose `.topbar` is allowed to shrink, so an extra flex box in
 * the middle of the tree — even one with no styles of its own — changes how
 * the overflow is distributed and moves the whole screen 14px. The phone tree
 * has to be identical, not equivalent.
 */
function StageOrColumn({ desktop, children }: { desktop: boolean; children: React.ReactNode }) {
  if (!desktop) return <>{children}</>;
  return <div className="wr-lobby" style={{ flex: 1, minHeight: 0 }}>{children}</div>;
}

interface SettingsPanelProps {
  gs: GameSettings;
  onUpdateSettings?: (settings: Partial<GameSettings>) => void;
}

function SettingsPanel({ gs, onUpdateSettings }: SettingsPanelProps) {
  /**
   * Every change goes to TWO places: local React state (so the sheet responds
   * instantly) and the SERVER (so it actually happens). The second half was
   * missing entirely — the whole sheet was a local no-op after createRoom.
   */
  const set = (s: Partial<GameSettings>) => {
    onUpdateSettings?.(s);
    websocketService.updateGameSettings(s as Record<string, any>);
  };
  const rules = gs.houseRules || defaultHouseRules;
  const setRule = (r: Partial<HouseRulesSettings>) => set({ houseRules: { ...rules, ...r } });
  const squeezeSeconds = rules.squeezeSeconds ?? 10;

  return (
    <div className="wr-col gap-5 mt-3">
      {/* Rounds */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Rounds</div>
        <div className="segmented">
          {[3, 5, 7, 10].map(r => (
            <button
              key={r}
              className={`seg ${gs.rounds === r ? 'active' : ''}`}
              onClick={() => set({ rounds: r })}
              type="button"
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Time per round */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Time per round</div>
        <div className="segmented">
          {[30, 60, 90].map(t => (
            <button
              key={t}
              className={`seg ${gs.timeLimit === t ? 'active' : ''}`}
              onClick={() => set({ timeLimit: t })}
              type="button"
            >
              {t}s
            </button>
          ))}
        </div>
      </div>

      {/* THE SQUEEZE — how long everyone else gets once the first player is
          done. Was hardcoded at 10s. The server only ever uses this to pull the
          deadline IN (min(endsAt, now + n)), so a long squeeze on a nearly-over
          round changes nothing — it can't hand time back. */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>The squeeze</div>
        <div className="segmented">
          {[5, 10, 15, 20, 30].map(s => (
            <button
              key={s}
              className={`seg ${squeezeSeconds === s ? 'active' : ''}`}
              onClick={() => setRule({ squeezeSeconds: s })}
              type="button"
            >
              {s}s
            </button>
          ))}
        </div>
        <div className="wr-text-3" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
          When the first player hits <b>I'M DONE</b>, everyone else gets{' '}
          <b style={{ color: 'var(--ink)' }}>{squeezeSeconds} seconds</b> to finish. Never more time
          than was already on the clock.
        </div>
      </div>

      {/* Categories (read-only chips for now — defaults locked) */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Categories</div>
        <div className="chip-set">
          {DEFAULT_CATEGORIES.map(cat => {
            const Icon = CATEGORY_ICONS[cat] || (() => null);
            return (
              <span key={cat} className="chip-cat active">
                <Icon size={12} /> {cat}
              </span>
            );
          })}
          <span className="chip-cat">
            <Lock size={12} /> Custom · Plus
          </span>
        </div>
      </div>

      {/* House rules toggles */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>House rules</div>
        {/* These bind to the REAL HouseRulesSettings fields. They previously
            wrote `allowBrands` / `allowNicknames`, which do not exist on the
            type — so both toggles were writing into the void and the backend
            never saw them. */}
        <div className="wr-col gap-2">
          <ToggleRow
            label="Proper nouns allowed"
            hint="Pikachu, Paris, Apple…"
            value={rules.allowProperNouns ?? true}
            onChange={v => setRule({ allowProperNouns: v })}
          />
          <ToggleRow
            label="Alternate spellings count"
            hint='Treat "Sam" as "Samuel"'
            value={rules.allowAlternateSpellings ?? true}
            onChange={v => setRule({ allowAlternateSpellings: v })}
          />
          {/* Off by default, and it should stay that way for most tables — it
              turns a wrong guess into an actual loss. The host override in the
              tally is the safety net when the validator is the one that's wrong. */}
          <ToggleRow
            label="Strict scoring"
            hint="Wrong answers lose 5 points instead of scoring 0"
            value={rules.strictScoring ?? false}
            onChange={v => setRule({ strictScoring: v })}
          />
        </div>
      </div>

      <div style={{ height: 16 }} />
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="card wr-between" style={{ padding: 14 }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{hint}</div>}
      </div>
      <button
        className={`wr-toggle ${value ? 'on' : ''}`}
        onClick={() => onChange(!value)}
        type="button"
        aria-pressed={value}
        aria-label={label}
      />
    </div>
  );
}

function formatCode(code: string): string {
  // Show the code exactly as the backend stores it. Earlier we inserted a
  // hyphen for "readability" — but the user then typed/copied the
  // hyphenated form back, and the backend's room map has no hyphens, so
  // the join silently timed out. Just show the raw code.
  return (code || '').toUpperCase();
}
